/**
 * 학교·학년 적용범위 선택(교사 제공 패널과 PDF 등록이 함께 쓴다). 학생 첫설정과 같은 /schools 목록(같은 stable school ID·학년)만 쓰고
 * 따로 하드코딩하지 않는다. 학교마다 학년 토글을 두고, 켠 (학교, 학년) 쌍만 담는다(다른 학교의 같은 학년으로 곱집합을 만들지 않는다).
 */
import { h, clear } from './dom.js';
import { currentGen, type Transport } from '../core/transport.js';
import { toggleButton } from './toggle-button.js';

export interface School { id: string; name: string; level: string; grades?: number[] }
export interface Pair { schoolId: string; grade: number }

export interface SchoolGradePicker {
  readonly node: HTMLElement;
  /** 켜 둔 쌍의 복사본 */
  pairs(): Pair[];
  nameOf(schoolId: string): string;
  /** 켠 쌍을 모두 끈다 */
  reset(): void;
  /** 서버 목록을 다시 읽는다(관리자가 추가한 학교 반영). 성공 여부를 돌려준다 */
  reload(): Promise<boolean>;
}

export function mountSchoolGradePicker(t: Transport, isLive: () => boolean, onChange: () => void = () => undefined, prefix = 'tp'): SchoolGradePicker {
  const gen = currentGen();
  const live = () => gen === currentGen() && isLive();
  let schools: School[] = [];
  const on: Pair[] = [];
  const node = h('div', { 'data-testid': `${prefix}-pairs` });
  const has = (sid: string, g: number) => on.some((p) => p.schoolId === sid && p.grade === g);

  const render = () => {
    clear(node);
    for (const sc of schools) {
      const row = h('div', { role: 'group', 'aria-label': `${sc.name} 학년`, class: 'toggle-group', 'data-testid': `${prefix}-school-${sc.id}` }, h('span', { class: 'label' }, sc.name));
      for (const g of sc.grades ?? [1, 2, 3]) {
        row.append(toggleButton(`${g}학년`, has(sc.id, g), `${prefix}-toggle-${sc.id}-${g}`, () => {
          const i = on.findIndex((p) => p.schoolId === sc.id && p.grade === g);
          if (i >= 0) on.splice(i, 1); else on.push({ schoolId: sc.id, grade: g });
          render(); onChange();
        }));
      }
      node.append(row);
    }
  };

  async function reload(): Promise<boolean> {
    const s = await t.request('GET', '/schools');
    if (!live() || s.kind !== 'http' || s.status !== 200) return false;
    schools = s.body['schools'] as School[];
    // 목록에서 사라졌거나 학년이 바뀐 쌍은 켜 둔 채 남기지 않는다
    for (let i = on.length - 1; i >= 0; i--) {
      const sc = schools.find((x) => x.id === on[i]!.schoolId);
      if (sc === undefined || !(sc.grades ?? [1, 2, 3]).includes(on[i]!.grade)) on.splice(i, 1);
    }
    render();
    return true;
  }

  void reload();
  return {
    node, pairs: () => on.map((p) => ({ ...p })), nameOf: (id) => schools.find((s) => s.id === id)?.name ?? '?',
    reset: () => { on.length = 0; render(); onChange(); }, reload,
  };
}
